import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match } from 'ts-pattern';
import { useIxcMutualfundsV1MutualFundsJobQueryServiceMutualFundsJobGet } from '@interxfi/api-client/generated/hooks/ixc-mutualfunds-v1-mutual-funds-job-query-service-mutual-funds-job-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcMutualfundsV1MutualFundsJobState } from '@interxfi/types';
import { TableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import {
  MutualFundsJobEventDrawer,
  MutualFundsJobEventSummary,
} from '../../../../../../../../../../features/mutual-funds/job/mutual-funds-job-event';
import { getMutualFundsJobStateListItems } from '../../../../../../../../../../features/mutual-funds/job/mutual-funds-job-list-items';
import { getMutualFundsJobDetailPath } from '../../../../../../../../../../features/mutual-funds/job/mutual-funds-job-routing';
import { MutualFundsJobStatusPill } from '../../../../../../../../../../features/mutual-funds/job/mutual-funds-job-status-pill';
import { MutualFundsTaskTable } from '../../../../../../../../../../features/mutual-funds/task/mutual-funds-task-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/mutualfunds/job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `mutualfunds-job-${params.jobId}`,
        label: 'Job',
        to: getMutualFundsJobDetailPath(params.jobId),
        suffix: (
          <CopyButton
            content={params.jobId}
            label="Copy Mutual Funds Job ID"
            tooltip={{ copy: params.jobId, copied: 'Copied Mutual Funds Job ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Mutual Funds Job'),
  component: MutualFundsJobDetail,
});

function getListItems(data: IxcMutualfundsV1MutualFundsJobState | undefined) {
  const listItems = getMutualFundsJobStateListItems(data);
  const stats = listItems.data.stats;

  return {
    left: [
      {
        id: 'main',
        items: [
          listItems.data.operationType,
          ...match(listItems.data.spec)
            .returnType<ReadonlyListItem[]>()
            .with({ '!type': 'dividendsCalculate' }, (s) => [
              s.value.assetId,
              s.value.dividendDate,
              s.value.dividendRate,
              s.value.rateFingerprint,
              s.value.rateTimestamp,
              s.value.expectedPaymentDate,
              ...(s.value.programId ? [s.value.programId] : []),
            ])
            .with({ '!type': 'dividendsPay' }, (s) => [s.value.assetId, s.value.firstDayOfMonth, s.value.endDate])
            .otherwise(() => []),
        ],
      },
      ...(stats
        ? [
            {
              id: 'stats',
              title: 'Stats',
              items: [
                stats.dividendsCalculate.expectedPositions,
                stats.dividendsCalculate.applied,
                stats.dividendsCalculate.lastCheckAt,
                stats.dividendsCalculate.stallChecks,
                stats.dividendsCalculate.requestsSent,
              ],
            },
          ]
        : []),
      {
        id: 'metrics',
        title: 'Metrics',
        items: [listItems.data.metrics.total, listItems.data.metrics.pending, listItems.data.metrics.succeeded, listItems.data.metrics.errored],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.mutualFundsJobId] as const satisfies ReadonlyListItem[],
  };
}

function getMenuItems(mutualFundsJobId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: MutualFundsJobEventDrawer,
        props: { request: { mutualFundsJobId } },
      },
    },
    actions: undefined,
  };
}

function TasksTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Tasks" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load mutual funds job', 'view this mutual funds job');

function MutualFundsJobDetail() {
  const { jobId } = Route.useParams();
  const request = { mutualFundsJobId: jobId } as const;
  const { data, error, isPending } = useIxcMutualfundsV1MutualFundsJobQueryServiceMutualFundsJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.mutualFundsJob), [data?.mutualFundsJob]);
  const entityMenuItems = useMemo(() => getMenuItems(jobId), [jobId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <MutualFundsJobStatusPill isPending={showPending} size="large" status={data?.mutualFundsJob?.status} />
            </HorizontalStack>

            <EntityActionMenu {...entityMenuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <MutualFundsJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <MutualFundsTaskTable HeaderActions={TasksTableHeader} mutualFundsJobId={jobId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
